import type { Person } from "../../src/web/types.ts";
import { folded } from "./maplist.ts";

// The People view's row of faces (features/people.md, "Whose list, picked by face"): who's in it and in
// what order, the name under each face, and what "Find someone" finds. Plain functions with type-only
// imports, so the server-side tests can use them too.

// What a person whose account was deleted is named, wherever they're the author (former.tsx).
export const FORMER_MEMBER = "a former member";

// Past this many people, a "Find someone" field starts the row.
export const FIND_PAST = 12;

// A person's first name: their name's first word ("Ana Sofía Hernández" is Ana).
export const firstName = (name: string): string => name.trim().split(/\s+/)[0] || name;

// Names compared as people read them: no capitals or accents, numbers by value.
const byName = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });

// A name's last word, its family name where it has one ("Hernández" in Ana Sofía Hernández).
const lastName = (name: string): string => name.trim().split(/\s+/).slice(1).at(-1) ?? "";

// You first, then everyone by first name, and people with the same first name by their last.
export function inOrder(members: Person[], you: string): Person[] {
  const others = members
    .filter((m) => m.id !== you)
    .sort(
      (a, b) =>
        byName.compare(firstName(a.name), firstName(b.name)) ||
        byName.compare(lastName(a.name), lastName(b.name)) ||
        byName.compare(a.name, b.name),
    );
  return [...members.filter((m) => m.id === you), ...others];
}

// The name under each face, by person: You for me; for everyone else their first name, with their last
// name's initial when someone else in the trip has the same first name ("Ana H." and "Ana L."), or their
// whole name when that doesn't tell them apart either.
export function faceNames(members: Person[], you: string): Map<string, string> {
  const others = members.filter((m) => m.id !== you);
  const count = (key: (p: Person) => string) => {
    const n = new Map<string, number>();
    for (const p of others) n.set(key(p), (n.get(key(p)) ?? 0) + 1);
    return n;
  };
  const first = (p: Person) => folded(firstName(p.name));
  const initialed = (p: Person) => {
    const last = lastName(p.name);
    return last ? `${firstName(p.name)} ${[...last][0]?.toUpperCase()}.` : firstName(p.name);
  };
  const firsts = count(first);
  const initials = count((p) => folded(initialed(p)));
  const out = new Map<string, string>();
  for (const m of members) {
    if (m.id === you) out.set(m.id, "You");
    else if ((firsts.get(first(m)) ?? 0) < 2) out.set(m.id, firstName(m.name));
    else if ((initials.get(folded(initialed(m))) ?? 0) < 2) out.set(m.id, initialed(m));
    else out.set(m.id, m.name.trim());
  }
  return out;
}

// The words of a name as "Find someone" compares them, without capitals or accents (Søren is "soren",
// Łukasz "lukasz"): each word whole, and each part of one joined by a hyphen or an apostrophe, so
// "xavier" finds François-Xavier and "sullivan" O'Sullivan.
const nameWords = (name: string) =>
  name
    .split(/\s+/)
    .flatMap((word) => [word, ...word.split(/[-‐-―'‘’ʼ]+/)])
    .map(folded)
    .filter((w) => w.length > 0);

// Whether a search finds this person: each of its words starts a word of their name ("ana s" finds Ana
// Sofía, "zoe" Zoë), or "you" for me.
export function finds(person: Person, query: string, you: string): boolean {
  const wanted = query
    .split(/\s+/)
    .map(folded)
    .filter((w) => w.length > 0);
  const theirs = [...nameWords(person.name), ...(person.id === you ? ["you"] : [])];
  return wanted.every((w) => theirs.some((t) => t.startsWith(w)));
}
